import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { ObjectIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    ResponseTimeStatsFieldsWithDefaults,
    ResponseTimeStatsSchemaTypes,
} from "../types/responseTimeStats.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ResponseTimeStats" as const;

/**
 * Computed response-time statistics for a device or zone, based on the slowest actuator.
 *
 * The target entity (device or zone) is identified via `foreignLookup`. A compound
 * unique index on `(foreignLookup.targetId, foreignLookup.targetCollection)` ensures
 * at most one stats document per entity.
 *
 * Fields are scoped to actuation (command dispatch → actuator fires). All timing values
 * are theoretical / model-derived. Fields for empirically measured latency are defined
 * below but commented out — uncomment them once we start collecting real samples.
 */
@index({ "foreignLookup.targetId": 1, "foreignLookup.targetCollection": 1 }, { unique: true })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: SCHEMA_KEY,
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<ResponseTimeStats>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ResponseTimeStats extends SchemaBase<ResponseTimeStats, ResponseTimeStatsFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * Links this document to the device or zone it was calculated for.
     * Set `targetCollection` to the relevant collection name (e.g. "devices" or "zones")
     * and `targetId` to the entity's node_id or string identifier.
     */
    @validate({ childClass: ForeignLookup, responseOnly: true })
    @prop({ type: () => ForeignLookup, required: true, _id: false })
    public foreignLookup!: ForeignLookup;

    // -------------------------------------------------------------------------
    // Theoretical / model-derived actuation timing fields
    // -------------------------------------------------------------------------

    /**
     * Theoretical 50th-percentile actuation delay: time from command dispatch
     * to actuator firing, based on the slowest actuator (ms).
     */
    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public theoreticalP50ActuationDelayMs!: number;

    /**
     * Theoretical 95th-percentile actuation delay (ms).
     */
    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public theoreticalP95ActuationDelayMs!: number;

    /**
     * Theoretical 99th-percentile actuation delay (ms).
     */
    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public theoreticalP99ActuationDelayMs!: number;

    /**
     * When these theoretical values were last recalculated.
     */
    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public lastCalculatedAt!: Date;

    static getters = {} as const satisfies Record<string, (self: ResponseTimeStats) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ResponseTimeStatsSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ResponseTimeStats, ResponseTimeStatsFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ResponseTimeStats.dtoValidators = getZodSchemaFromClass(
    ResponseTimeStats,
) as unknown as DtoValidatorTypes<ResponseTimeStatsSchemaTypes>;
